import { useState, useEffect } from "react";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import {
  Plus,
  Loader2,
  CheckCircle,
  Clock,
  AlertTriangle,
  Send,
  Building2,
  Users,
  Briefcase,
  Phone,
  Mail,
  MapPin,
  DollarSign,
  Shield,
  X,
  Link,
  Copy,
  ExternalLink,
  Wrench,
  ClipboardList,
  Eye,
  Navigation,
  Timer,
  Edit,
  Trash2,
  Table,
  LayoutGrid,
  MessageSquare,
  Search,
  FileSignature,
  FileText,
  CheckCircle2,
  ShieldAlert,
  Download,
  Receipt
} from "lucide-react";
import SupplierLedgerDialog from "@/components/admin/SupplierLedgerDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

export default function ContractorManagement() {
  const [contractors, setContractors] = useState([]);
  const [ledgerContractor, setLedgerContractor] = useState(null);
  const [categories, setCategories] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDispatchModal, setShowDispatchModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [selectedContractors, setSelectedContractors] = useState([]);
  const [dispatchNotes, setDispatchNotes] = useState("");
  const [isEmergency, setIsEmergency] = useState(false);
  const [warrantyOverrideCode, setWarrantyOverrideCode] = useState(null);
  const [generatingOverride, setGeneratingOverride] = useState(false);
  const [formData, setFormData] = useState({
    name: "", company_name: "", email: "", phone: "", categories: [], address: "", city: "", state: "", zip_code: "", website: "", hourly_rate: "", notes: ""
  });
  const [setupLinkModal, setSetupLinkModal] = useState(false);
  const [setupLink, setSetupLink] = useState("");
  const [setupLinkData, setSetupLinkData] = useState(null);
  const [generatingSetupLink, setGeneratingSetupLink] = useState(false);
  const [selectedContractorForSetup, setSelectedContractorForSetup] = useState(null);
  const [showAssignJobModal, setShowAssignJobModal] = useState(false);
  const [selectedContractorForAssign, setSelectedContractorForAssign] = useState(null);
  const [assignJobType, setAssignJobType] = useState("maintenance"); // maintenance or project
  const [availableJobs, setAvailableJobs] = useState([]);
  const [selectedJobId, setSelectedJobId] = useState("");
  const [assignNotes, setAssignNotes] = useState("");
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [showContractorDetail, setShowContractorDetail] = useState(false);
  const [contractorDetail, setContractorDetail] = useState(null);
  const [activeContractors, setActiveContractors] = useState([]);
  const [loadingActiveContractors, setLoadingActiveContractors] = useState(false);
  const [viewMode, setViewMode] = useState("table"); // "table" or "grid"
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingContractor, setEditingContractor] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [contractorToDelete, setContractorToDelete] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);

  // "Also open in Outlook" toggle for the Add Contractor modal
  const [openInOutlook, setOpenInOutlook] = useState(false);
  // Manual-add toggle: skip the activation email entirely. Used when admin is
  // adding a contractor we already work with offline.
  const [skipSetupEmail, setSkipSetupEmail] = useState(false);
  const [emailContractor, setEmailContractor] = useState(null);
  const [fullProfile, setFullProfile] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  
  // Agreement Invitation State
  const [showAgreementModal, setShowAgreementModal] = useState(false);
  const [agreementFormData, setAgreementFormData] = useState({
    contractor_name: "", contractor_company: "", contractor_email: "", contractor_address: "", contractor_phone: "", trade_type: "", notes: ""
  });
  const [sendingAgreement, setSendingAgreement] = useState(false);
  const [agreementResult, setAgreementResult] = useState(null);
  
  const { token } = useAuth();

  // Fetch active contractor locations (GPS check-ins)
  const fetchActiveContractors = async () => {
    try {
      setLoadingActiveContractors(true);
      const res = await fetch(`${API}/admin/contractors/active-locations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setActiveContractors(data.checkins || []);
      }
    } catch (err) {
      console.error("Failed to fetch active contractors:", err);
    } finally {
      setLoadingActiveContractors(false);
    }
  };

  // Auto-refresh active contractors every 30 seconds
  useEffect(() => {
    if (token) {
      fetchActiveContractors();
      const interval = setInterval(fetchActiveContractors, 30000);
      return () => clearInterval(interval);
    }
  }, [token]);

  const generateWarrantyOverride = async () => {
    if (!selectedTicket?.is_warranty) return;
    try {
      setGeneratingOverride(true);
      const res = await fetch(`${API}/admin/warranty-override/${selectedTicket.id}?reason=Admin%20authorized%20billing`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setWarrantyOverrideCode(data.override_code);
        toast.success(`Override code generated: ${data.override_code}`);
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to generate override code");
      }
    } catch (err) {
      toast.error("Failed to generate override code");
    } finally {
      setGeneratingOverride(false);
    }
  };

  const generateSetupLink = async (contractor) => {
    setSelectedContractorForSetup(contractor);
    setGeneratingSetupLink(true);
    try {
      const res = await fetch(`${API}/admin/contractors/${contractor.id}/generate-setup-link`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        // Set setupLinkData with the same structure as the add contractor flow
        setSetupLinkData({
          setup_link: data.setup_link,
          email_sent: data.email_sent,
          contractor: {
            id: contractor.id,
            name: contractor.name,
            company_name: contractor.company_name,
            email: contractor.email
          }
        });
        setSetupLink(data.setup_link);
        setSetupLinkModal(true);
        if (data.email_sent) {
          toast.success(`Setup link emailed to ${contractor.email}`);
        } else {
          toast.success("Setup link generated! Copy and send via email.");
        }
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to generate setup link");
      }
    } catch (err) {
      toast.error("Failed to generate setup link");
    } finally {
      setGeneratingSetupLink(false);
    }
  };

  const copySetupLink = () => {
    navigator.clipboard.writeText(setupLink);
    toast.success("Link copied to clipboard!");
  };

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [contRes, ticketRes] = await Promise.all([
        fetch(`${API}/contractors/profiles`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/maintenance-tickets?status=pending`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      if (contRes.ok) {
        const data = await contRes.json();
        setContractors(data.contractors || []);
        setCategories(data.categories || []);
      }
      if (ticketRes.ok) {
        const data = await ticketRes.json();
        setTickets(data.tickets || []);
      }
    } catch (err) { toast.error("Failed to load data"); }
    finally { setLoading(false); }
  };

  const handleAddContractor = async () => {
    if (!formData.name || !formData.email || !formData.company_name) {
      toast.error("Please fill required fields");
      return;
    }
    try {
      // Clean up form data - convert empty strings to null for optional fields
      const cleanedData = {
        ...formData,
        hourly_rate: formData.hourly_rate ? parseFloat(formData.hourly_rate) : null,
        address: formData.address || null,
        city: formData.city || null,
        state: formData.state || null,
        zip_code: formData.zip_code || null,
        website: formData.website || null,
        notes: formData.notes || null,
        categories: formData.categories || [],
        skip_setup_email: skipSetupEmail,
      };
      
      const res = await fetch(`${API}/contractors/profiles`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(cleanedData)
      });
      if (res.ok) {
        const data = await res.json();
        setSetupLinkData(data);
        setShowAddModal(false);
        setSetupLinkModal(true);
        const capturedForm = { ...formData };
        const wantOutlook = openInOutlook;
        const wasManual = skipSetupEmail;
        setFormData({ name: "", company_name: "", email: "", phone: "", categories: [], address: "", city: "", state: "", zip_code: "", website: "", hourly_rate: "", notes: "" });
        setSkipSetupEmail(false);
        fetchData();
        // Show appropriate toast message
        if (wasManual) {
          toast.success(`${capturedForm.company_name || "Contractor"} added & approved manually — no email sent.`);
        } else if (data.email_sent) {
          toast.success(`Setup email sent to ${capturedForm.email}!`);
        } else {
          toast.success("Contractor added! Setup link generated (email may be pending).");
        }
        // Also fire an Outlook compose draft if the admin opted in (and didn't skip emails)
        if (!wasManual && wantOutlook && data.setup_link) {
          openContractorInvitationInOutlook({
            contactName: capturedForm.name,
            companyName: capturedForm.company_name,
            recipientEmail: capturedForm.email,
            setupLink: data.setup_link,
          });
        }
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to add");
      }
    } catch (err) { toast.error("Failed to add contractor"); }
  };

  // Build a mailto: link with the contractor invitation copy and trigger the
  // admin's default mail client (Outlook desktop / web / Apple Mail).
  const openContractorInvitationInOutlook = ({ contactName, companyName, recipientEmail, setupLink }) => {
    const subject = "Invitation: Join the AskIsaac Contractor Network via Sterile Mate";
    const body = [
      `Hi ${contactName || "there"},`,
      ``,
      `Welcome to AskIsaac, the official technology platform for healthcare infrastructure dispatch and trades developed by Sterile Mate.`,
      ``,
      `Through AskIsaac, we provide our healthcare partners with a real-time bridge to verified contractors. When a hospital partner submits a work order — plumbing, electrical, HVAC, EVS, water treatment, capital projects — the platform alerts you instantly. To participate, simply review the dispatch, submit your bid or accept the rate-card, and AskIsaac routes the job assignment directly back to the facility.`,
      ``,
      `${companyName || "Your team"} has been invited to join as a verified contractor partner. To complete your account setup and begin receiving work orders, please use the secure link below:`,
      ``,
      `Complete Your Account Setup:`,
      setupLink,
      ``,
      `What to Expect:`,
      `  - Secure Access: Set your password, upload your license, COI & W-9, and review our terms.`,
      `  - Real-Time Alerts: Connect your Telegram to @askisaac_bot for instant work-order notifications.`,
      `  - Direct Dispatch: Start receiving and responding to hospital work orders immediately.`,
      ``,
      `Note: This invitation link will expire in 7 days.`,
      ``,
      `We look forward to working with you to streamline healthcare infrastructure.`,
      ``,
      `Best regards,`,
      `George Ivory Broughton III`,
      `CEO / President · Sterile Mate LLC`,
      `(281) 456-3458 · george.broughton@sterilemate.com`,
    ].join("\r\n");
    const mailto = `mailto:${encodeURIComponent(recipientEmail || "")}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailto;
  };

  // Edit contractor
  const openEditModal = (contractor) => {
    setEditingContractor(contractor);
    setFormData({
      name: contractor.name || contractor.contact_name || "",
      company_name: contractor.company_name || "",
      email: contractor.email || "",
      phone: contractor.phone || "",
      categories: contractor.categories || [],
      address: contractor.address || "",
      hourly_rate: contractor.hourly_rate?.toString() || ""
    });
    setShowEditModal(true);
  };

  const handleEditContractor = async () => {
    if (!editingContractor) return;
    try {
      const cleanedData = {
        ...formData,
        hourly_rate: formData.hourly_rate ? parseFloat(formData.hourly_rate) : null,
        address: formData.address || null,
        categories: formData.categories || []
      };
      
      const res = await fetch(`${API}/contractors/profiles/${editingContractor.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(cleanedData)
      });
      if (res.ok) {
        toast.success("Contractor updated");
        setShowEditModal(false);
        setEditingContractor(null);
        setFormData({ name: "", company_name: "", email: "", phone: "", categories: [], address: "", hourly_rate: "" });
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to update");
      }
    } catch (err) { toast.error("Failed to update contractor"); }
  };

  // Delete contractor
  const openDeleteConfirm = (contractor) => {
    setContractorToDelete(contractor);
    setShowDeleteConfirm(true);
  };

  const handleDeleteContractor = async () => {
    if (!contractorToDelete) return;
    try {
      const res = await fetch(`${API}/admin/contractors/${contractorToDelete.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success("Contractor removed");
        setShowDeleteConfirm(false);
        setContractorToDelete(null);
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to delete");
      }
    } catch (err) { toast.error("Failed to delete contractor"); }
  };

  // Email contractor directly
  const openEmailModal = (contractor) => {
    setEmailContractor(contractor);
    setEmailSubject("");
    setEmailBody("");
    setShowEmailModal(true);
  };

  const handleSendEmail = () => {
    // The on-platform email gateway is currently blocked by Resend domain
    // verification. Until that's done, we open the user's own Outlook with
    // the message pre-filled — they hit Send.
    if (!emailContractor || !emailSubject || !emailBody) {
      toast.error("Please fill in subject and message");
      return;
    }
    const url =
      `mailto:${emailContractor.email}` +
      `?subject=${encodeURIComponent(emailSubject)}` +
      `&body=${encodeURIComponent(emailBody)}`;
    window.open(url, "_blank");
    toast.success(`Opened email to ${emailContractor.email} in your mail client`);
    setShowEmailModal(false);
    setEmailContractor(null);
    setEmailSubject("");
    setEmailBody("");
  };

  // Send Agreement Invitation
  const handleSendAgreementInvitation = async () => {
    if (!agreementFormData.contractor_name || !agreementFormData.contractor_company || !agreementFormData.contractor_email) {
      toast.error("Please fill in required fields");
      return;
    }
    setSendingAgreement(true);
    try {
      const res = await fetch(`${API}/contractor-compliance/invite-contractor`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(agreementFormData)
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setAgreementResult(data);
        toast.success(data.message || "Agreement invitation sent!");
        // Clear form but keep modal open to show result
      } else {
        toast.error(data.message || data.detail || "Failed to send invitation");
      }
    } catch (err) { 
      toast.error("Failed to send agreement invitation"); 
    }
    finally { setSendingAgreement(false); }
  };

  const resetAgreementModal = () => {
    setShowAgreementModal(false);
    setAgreementFormData({
      contractor_name: "", contractor_company: "", contractor_email: "", contractor_address: "", contractor_phone: "", trade_type: "", notes: ""
    });
    setAgreementResult(null);
  };

  // Filter contractors by search term
  const filteredContractors = contractors.filter(c => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      (c.name || "").toLowerCase().includes(term) ||
      (c.company_name || "").toLowerCase().includes(term) ||
      (c.email || "").toLowerCase().includes(term) ||
      (c.phone || "").toLowerCase().includes(term)
    );
  });

  const handleDispatch = async () => {
    if (!selectedTicket || selectedContractors.length === 0) {
      toast.error("Please select at least one contractor");
      return;
    }
    try {
      const res = await fetch(`${API}/maintenance-tickets/${selectedTicket.id}/dispatch`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          contractor_ids: selectedContractors,
          notes: dispatchNotes,
          is_emergency: isEmergency,
          warranty_override_code: warrantyOverrideCode
        })
      });
      if (res.ok) {
        toast.success("Ticket dispatched");
        setShowDispatchModal(false);
        setSelectedTicket(null);
        setSelectedContractors([]);
        setDispatchNotes("");
        setIsEmergency(false);
        setWarrantyOverrideCode(null);
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to dispatch");
      }
    } catch (err) { toast.error("Dispatch failed"); }
  };

  const openAssignJobModal = async (contractor) => {
    setSelectedContractorForAssign(contractor);
    setAssignJobType("maintenance");
    setSelectedJobId("");
    setAssignNotes("");
    setShowAssignJobModal(true);
    fetchAvailableJobs("maintenance");
  };

  const fetchAvailableJobs = async (jobType) => {
    setLoadingJobs(true);
    try {
      if (jobType === "maintenance") {
        // Fetch maintenance requests that can be assigned (in triage or dispatch stage)
        const res = await fetch(`${API}/maintenance/pipeline`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          const pipeline = data.pipeline || {};
          // Get requests from intake, triage, and dispatch stages
          const jobs = [
            ...(pipeline.intake || []),
            ...(pipeline.triage || []),
            ...(pipeline.dispatch || [])
          ].map(j => ({
            id: j.id,
            label: `#${j.id} - ${j.type || j.request_type || 'Maintenance'} - ${j.hospital_name || j.facility || 'N/A'}`,
            type: j.type || j.request_type,
            hospital: j.hospital_name || j.facility,
            priority: j.priority,
            stage: j.stage
          }));
          setAvailableJobs(jobs);
        }
      } else {
        // Fetch projects that can be assigned
        const res = await fetch(`${API}/projects`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          const jobs = (data.projects || [])
            .filter(p => p.status !== 'completed' && p.status !== 'cancelled')
            .map(p => ({
              id: p.id,
              label: `${p.name} - ${p.hospital_name || 'N/A'}`,
              type: p.type || 'Project',
              hospital: p.hospital_name,
              status: p.status
            }));
          setAvailableJobs(jobs);
        }
      }
    } catch (error) {
      console.error("Failed to fetch jobs:", error);
      setAvailableJobs([]);
    } finally {
      setLoadingJobs(false);
    }
  };

  const handleJobTypeChange = (type) => {
    setAssignJobType(type);
    setSelectedJobId("");
    fetchAvailableJobs(type);
  };

  const handleAssignJob = async (forceAssign = false) => {
    if (!selectedJobId || !selectedContractorForAssign) {
      toast.error("Please select a job to assign");
      return;
    }

    try {
      const endpoint = assignJobType === "maintenance" 
        ? `${API}/admin/maintenance-requests/${selectedJobId}/assign-contractor`
        : `${API}/admin/projects/${selectedJobId}/assign-contractor`;
      
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json", 
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({
          contractor_id: selectedContractorForAssign.id,
          contractor_name: selectedContractorForAssign.name || selectedContractorForAssign.company_name,
          notes: assignNotes,
          force_assign: forceAssign,
        })
      });

      if (res.ok) {
        toast.success(
          (forceAssign ? "Override applied — " : "") +
          `${assignJobType === 'maintenance' ? 'Maintenance request' : 'Project'} assigned to ${selectedContractorForAssign.name || selectedContractorForAssign.company_name}`
        );
        setShowAssignJobModal(false);
        setSelectedContractorForAssign(null);
        setSelectedJobId("");
        setAssignNotes("");
        fetchData();
      } else {
        const err = await res.json();
        const msg = err.detail || "Failed to assign job";
        // If the failure is the verification gate, give the admin an override path.
        if (!forceAssign && /not yet verified/i.test(msg)) {
          if (window.confirm(
            `${msg}\n\nThis contractor has not completed document verification. ` +
            "Override and assign anyway? (This will be logged in the audit trail.)"
          )) {
            return handleAssignJob(true);
          }
        } else {
          toast.error(msg);
        }
      }
    } catch (error) {
      toast.error("Failed to assign job");
    }
  };

  const openContractorDetail = async (contractor) => {
    setContractorDetail(contractor);
    setShowContractorDetail(true);
    setFullProfile(null);

    // Fetch assignments
    try {
      const res = await fetch(`${API}/admin/contractors/${contractor.id}/assignments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setContractorDetail((prev) => ({ ...(prev || contractor), assignments: data.assignments || [] }));
      }
    } catch (error) {
      console.error("Failed to fetch assignments:", error);
    }

    // Fetch full profile (documents + verification checklist)
    try {
      const res2 = await fetch(`${API}/admin/contractors/${contractor.id}/full-profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res2.ok) {
        const profile = await res2.json();
        setFullProfile(profile);
      }
    } catch (error) {
      console.error("Failed to fetch full profile:", error);
    }
  };

  // Reload the full profile (after a verify/unverify action)
  const refreshFullProfile = async (contractorId) => {
    try {
      const res = await fetch(`${API}/admin/contractors/${contractorId}/full-profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setFullProfile(await res.json());
    } catch (error) { /* ignore */ }
  };

  const handleMarkVerified = async () => {
    if (!contractorDetail) return;
    setVerifying(true);
    try {
      const res = await fetch(`${API}/admin/contractors/${contractorDetail.id}/verify`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success(`${contractorDetail.name || "Contractor"} marked as Verified`);
        await refreshFullProfile(contractorDetail.id);
        await fetchContractors();
      } else {
        toast.error("Failed to mark verified");
      }
    } catch (e) { toast.error("Failed to mark verified"); }
    finally { setVerifying(false); }
  };

  const handleRevokeVerification = async () => {
    if (!contractorDetail) return;
    if (!window.confirm("Revoke this contractor's verified status? They will no longer receive new job assignments.")) return;
    setVerifying(true);
    try {
      const res = await fetch(`${API}/admin/contractors/${contractorDetail.id}/unverify`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success("Verification revoked");
        await refreshFullProfile(contractorDetail.id);
        await fetchContractors();
      } else {
        toast.error("Failed to revoke verification");
      }
    } catch (e) { toast.error("Failed to revoke verification"); }
    finally { setVerifying(false); }
  };

  // Upload a compliance document on behalf of the contractor
  const uploadContractorDocument = async (file, documentType, expirationDate = null) => {
    if (!contractorDetail || !file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("document_type", documentType);
    if (expirationDate) fd.append("expiration_date", expirationDate);
    try {
      const res = await fetch(`${API}/admin/contractors/${contractorDetail.id}/upload-document`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (res.ok) {
        toast.success("Document uploaded");
        await refreshFullProfile(contractorDetail.id);
      } else {
        const e = await res.json().catch(() => ({}));
        toast.error(e.detail || "Upload failed");
      }
    } catch (e) {
      toast.error("Upload failed");
    }
  };

  const deleteContractorDocument = async (documentId) => {
    if (!contractorDetail || !documentId) return;
    if (!window.confirm("Delete this document?")) return;
    try {
      const res = await fetch(
        `${API}/admin/contractors/${contractorDetail.id}/documents/${documentId}`,
        { method: "DELETE", headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        toast.success("Document removed");
        await refreshFullProfile(contractorDetail.id);
      } else {
        toast.error("Delete failed");
      }
    } catch (e) { toast.error("Delete failed"); }
  };

  // Open the activation email in the admin's own Outlook (george.broughton@sterilemate.com)
  // The user wanted this approach instead of relying on the broken Resend domain path.
  const openActivationEmail = async (contractor, useOutlookWeb = false) => {
    try {
      const res = await fetch(`${API}/admin/contractors/${contractor.id}/activation-mailto`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        toast.error("Could not build activation email");
        return;
      }
      const data = await res.json();
      window.open(useOutlookWeb ? data.outlook_web_url : data.mailto_url, "_blank");
      toast.success(`Opened activation email for ${contractor.email} in your mail client`);
    } catch (e) {
      toast.error("Failed to open email");
    }
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-slate-900">Contractor Network</h2>
        <div className="flex gap-2">
          <Button onClick={() => setShowAgreementModal(true)} className="bg-emerald-600 hover:bg-emerald-700">
            <FileSignature className="w-4 h-4 mr-2" />
            Send Agreement
          </Button>
          <Button onClick={() => setShowAddModal(true)} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-2" />
            Add Contractor
          </Button>
        </div>
      </div>

      <Tabs defaultValue="contractors">
        <TabsList>
          <TabsTrigger value="contractors">
            <Briefcase className="w-4 h-4 mr-2" />
            Contractors ({contractors.length})
          </TabsTrigger>
          <TabsTrigger value="active">
            <Navigation className="w-4 h-4 mr-2" />
            Live On-Site ({activeContractors.length})
          </TabsTrigger>
          <TabsTrigger value="dispatch">
            <Send className="w-4 h-4 mr-2" />
            Pending Dispatch ({tickets.length})
          </TabsTrigger>
        </TabsList>

        {/* Live On-Site Tab - Contractors currently checked in */}
        <TabsContent value="active" className="mt-4">
          <div className="bg-gradient-to-br from-emerald-50 to-cyan-50 border border-emerald-200 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-emerald-900 flex items-center gap-2">
                <MapPin className="w-5 h-5" />
                Contractors Currently On Site
              </h3>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={fetchActiveContractors}
                disabled={loadingActiveContractors}
              >
                {loadingActiveContractors ? <Loader2 className="w-4 h-4 animate-spin" /> : "Refresh"}
              </Button>
            </div>
            
            {activeContractors.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Navigation className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No contractors currently on site</p>
                <p className="text-sm">Contractors will appear here when they check in via GPS</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {activeContractors.map(checkin => (
                  <div key={checkin.id} className="bg-white border border-emerald-200 rounded-lg p-4 shadow-sm">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="font-semibold text-slate-900">{checkin.contractor_name}</h4>
                        <p className="text-sm text-slate-500">{checkin.hospital_name}</p>
                        {checkin.facility_name && (
                          <p className="text-xs text-slate-400">{checkin.facility_name}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                        On Site
                      </div>
                    </div>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2 text-slate-600">
                        <Clock className="w-4 h-4" />
                        <span>Checked in: {new Date(checkin.check_in_time).toLocaleTimeString()}</span>
                      </div>
                      <div className="flex items-center gap-2 text-emerald-600 font-medium">
                        <Timer className="w-4 h-4" />
                        <span>Duration: {checkin.current_duration_formatted || "Calculating..."}</span>
                      </div>
                      {checkin.job_title && (
                        <div className="flex items-start gap-2 text-slate-600">
                          <Wrench className="w-4 h-4 mt-0.5" />
                          <span className="truncate">{checkin.job_title}</span>
                        </div>
                      )}
                    </div>
                    
                    {/* GPS coordinates */}
                    <div className="mt-3 pt-3 border-t border-slate-100">
                      <a 
                        href={`https://www.google.com/maps?q=${checkin.check_in_location?.latitude},${checkin.check_in_location?.longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                      >
                        <MapPin className="w-3 h-3" />
                        View on Google Maps
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="contractors" className="mt-4">
          {/* Search and View Toggle */}
          <div className="flex items-center justify-between mb-4">
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search contractors..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant={viewMode === "table" ? "default" : "outline"}
                size="sm"
                onClick={() => setViewMode("table")}
              >
                <Table className="w-4 h-4 mr-1" />
                Table
              </Button>
              <Button
                variant={viewMode === "grid" ? "default" : "outline"}
                size="sm"
                onClick={() => setViewMode("grid")}
              >
                <LayoutGrid className="w-4 h-4 mr-1" />
                Grid
              </Button>
            </div>
          </div>

          {/* Table View */}
          {viewMode === "table" && (
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Contractor</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Contact</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Categories</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Rate</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Status</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredContractors.map(c => (
                    <tr key={c.id} className="hover:bg-slate-50" data-testid={`contractor-row-${c.id}`}>
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-slate-900">{c.name || c.contact_name}</p>
                          <p className="text-sm text-slate-500">{c.company_name}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="space-y-1">
                          <div className="flex items-center gap-1 text-sm text-slate-600">
                            <Mail className="w-3 h-3" />
                            {c.email}
                          </div>
                          <div className="flex items-center gap-1 text-sm text-slate-500">
                            <Phone className="w-3 h-3" />
                            {c.phone || "N/A"}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {c.categories?.slice(0, 2).map(cat => (
                            <span key={cat} className="px-2 py-0.5 bg-blue-50 text-blue-600 rounded text-xs">{cat}</span>
                          ))}
                          {c.categories?.length > 2 && (
                            <span className="px-2 py-0.5 bg-slate-100 text-slate-500 rounded text-xs">+{c.categories.length - 2}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {c.hourly_rate ? (
                          <span className="text-sm font-medium text-slate-700">${c.hourly_rate}/hr</span>
                        ) : (
                          <span className="text-sm text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-1">
                          {c.is_verified ? (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium inline-flex items-center gap-1 w-fit bg-emerald-100 text-emerald-700" data-testid={`verified-badge-${c.id}`}>
                              <CheckCircle2 className="w-3 h-3" /> Verified
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium inline-flex items-center gap-1 w-fit bg-amber-100 text-amber-700" data-testid={`unverified-badge-${c.id}`}>
                              <ShieldAlert className="w-3 h-3" /> Pending Verify
                            </span>
                          )}
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium inline-flex items-center w-fit ${c.is_available ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                            {c.is_available ? 'Available' : 'Busy'}
                          </span>
                          {!c.password_hash && (
                            <span className="px-2 py-0.5 rounded-full text-xs bg-amber-50 text-amber-700 inline-flex items-center w-fit">
                              Not Activated
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openContractorDetail(c)} title="View Full Profile" data-testid={`view-profile-${c.id}`}>
                            <Eye className="w-4 h-4 text-slate-600" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => openEmailModal(c)} title="Send Email">
                            <MessageSquare className="w-4 h-4 text-blue-500" />
                          </Button>
                          <Button variant="ghost" size="sm"
                            onClick={() => setLedgerContractor({ id: c.id, name: c.name || c.company_name })}
                            title="Payment ledger — invoices, payments and what is still outstanding"
                            data-testid={`contractor-ledger-btn-${c.id}`}>
                            <Receipt className="w-4 h-4 text-sky-600" />
                          </Button>
                          {!c.password_hash && (
                            <Button variant="ghost" size="sm" onClick={() => openActivationEmail(c)} title="Send Activation Email via Outlook" data-testid={`send-activation-${c.id}`}>
                              <Mail className="w-4 h-4 text-emerald-600" />
                            </Button>
                          )}
                          {!c.password_hash && (
                            <Button variant="ghost" size="sm" onClick={() => generateSetupLink(c)} title="Generate Setup Link" disabled={generatingSetupLink}>
                              <Link className="w-4 h-4 text-emerald-500" />
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => openAssignJobModal(c)} title="Assign Job">
                            <Briefcase className="w-4 h-4 text-purple-500" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => openEditModal(c)} title="Edit">
                            <Edit className="w-4 h-4 text-slate-500" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => openDeleteConfirm(c)} title="Delete">
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredContractors.length === 0 && (
                <div className="text-center py-8 text-slate-500">
                  {searchTerm ? "No contractors match your search" : "No contractors added yet"}
                </div>
              )}
            </div>
          )}

          {/* Grid View */}
          {viewMode === "grid" && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredContractors.map(c => (
                <div key={c.id} className="bg-white border border-slate-200 rounded-xl p-4 hover:shadow-md transition-shadow" data-testid={`contractor-card-${c.id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-slate-900">{c.name || c.contact_name}</h3>
                      <p className="text-sm text-slate-500">{c.company_name}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${c.is_available ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                        {c.is_available ? 'Available' : 'Busy'}
                      </span>
                      {!c.password_hash && (
                        <span className="px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-700">
                          Not Activated
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-slate-600">
                      <Mail className="w-4 h-4" />
                      {c.email}
                    </div>
                    <div className="flex items-center gap-2 text-slate-600">
                      <Phone className="w-4 h-4" />
                      {c.phone || 'N/A'}
                    </div>
                    {c.hourly_rate && (
                      <div className="flex items-center gap-2 text-slate-600">
                        <DollarSign className="w-4 h-4" />
                        ${c.hourly_rate}/hr
                      </div>
                    )}
                  </div>
                  {c.categories?.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1">
                      {c.categories.slice(0, 3).map(cat => (
                        <span key={cat} className="px-2 py-0.5 bg-blue-50 text-blue-600 rounded text-xs">{cat}</span>
                      ))}
                    </div>
                  )}
                  {/* Send Setup Link Button - show if not activated */}
                  {!c.password_hash && (
                    <div className="mt-4 pt-3 border-t border-slate-100">
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="w-full text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                        onClick={() => generateSetupLink(c)}
                        disabled={generatingSetupLink}
                      >
                        {generatingSetupLink && selectedContractorForSetup?.id === c.id ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Link className="w-4 h-4 mr-2" />
                        )}
                        Send Setup Link
                      </Button>
                    </div>
                  )}
                  {/* Action Buttons */}
                  <div className="mt-4 pt-3 border-t border-slate-100 flex gap-2">
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => openEmailModal(c)}
                      title="Send Email"
                    >
                      <MessageSquare className="w-4 h-4" />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => openContractorDetail(c)}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => openEditModal(c)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button 
                      size="sm" 
                      className="flex-1 bg-blue-600 hover:bg-blue-700"
                      onClick={() => openAssignJobModal(c)}
                    >
                      <Briefcase className="w-4 h-4 mr-1" />
                      Assign
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="text-red-500 hover:bg-red-50"
                      onClick={() => openDeleteConfirm(c)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
              {filteredContractors.length === 0 && (
                <div className="col-span-full text-center py-8 text-slate-500">
                  {searchTerm ? "No contractors match your search" : "No contractors added yet"}
                </div>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="dispatch" className="mt-4">
          <div className="space-y-3">
            {tickets.map(ticket => (
              <div key={ticket.id} className="bg-white border border-slate-200 rounded-lg p-4 flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm text-slate-500">#{ticket.id}</span>
                    {ticket.is_warranty && (
                      <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs flex items-center gap-1">
                        <Shield className="w-3 h-3" />
                        Warranty
                      </span>
                    )}
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      ticket.priority === 'emergency' ? 'bg-red-100 text-red-700' :
                      ticket.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                      'bg-blue-100 text-blue-700'
                    }`}>
                      {ticket.priority?.toUpperCase()}
                    </span>
                  </div>
                  <p className="font-medium text-slate-900 mt-1">{ticket.type}</p>
                  <p className="text-sm text-slate-500">{ticket.hospital_name}</p>
                </div>
                <Button onClick={() => { setSelectedTicket(ticket); setShowDispatchModal(true); }} variant="outline">
                  <Send className="w-4 h-4 mr-2" />
                  Dispatch
                </Button>
              </div>
            ))}
            {tickets.length === 0 && (
              <div className="text-center py-8 text-slate-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-3 text-emerald-500" />
                <p>All tickets dispatched!</p>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Add Contractor Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wrench className="w-5 h-5 text-violet-600" />
              Invite New Contractor
            </DialogTitle>
            <DialogDescription>Enter contractor details. They will receive a setup link via email to complete their account.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Company Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Contact Name *</Label>
                <Input value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} placeholder="e.g. John Smith" />
              </div>
              <div>
                <Label>Company Name *</Label>
                <Input value={formData.company_name} onChange={e => setFormData({...formData, company_name: e.target.value})} placeholder="e.g. ABC Services" />
              </div>
            </div>
            
            {/* Contact Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Email *</Label>
                <Input type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} placeholder="john@company.com" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} placeholder="(800) 988-3892" />
              </div>
            </div>
            
            <div>
              <Label>Website</Label>
              <Input value={formData.website} onChange={e => setFormData({...formData, website: e.target.value})} placeholder="https://www.company.com" />
            </div>
            
            {/* Address */}
            <div className="border-t pt-4">
              <Label className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                Address
              </Label>
              <div className="space-y-3">
                <Input value={formData.address} onChange={e => setFormData({...formData, address: e.target.value})} placeholder="Street Address" />
                <div className="grid grid-cols-3 gap-3">
                  <Input value={formData.city} onChange={e => setFormData({...formData, city: e.target.value})} placeholder="City" />
                  <Input value={formData.state} onChange={e => setFormData({...formData, state: e.target.value})} placeholder="State" maxLength={2} />
                  <Input value={formData.zip_code} onChange={e => setFormData({...formData, zip_code: e.target.value})} placeholder="ZIP Code" />
                </div>
              </div>
            </div>
            
            {/* Rate & Category */}
            <div className="border-t pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Category</Label>
                  <Select value={formData.categories?.[0] || ""} onValueChange={val => setFormData({...formData, categories: val ? [val] : []})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Hourly Rate ($)</Label>
                  <Input type="number" value={formData.hourly_rate} onChange={e => setFormData({...formData, hourly_rate: e.target.value})} placeholder="75" />
                </div>
              </div>
            </div>
            
            {/* Notes */}
            <div className="border-t pt-4">
              <Label className="flex items-center gap-2">
                <ClipboardList className="w-4 h-4" />
                Account Notes
              </Label>
              <Textarea
                value={formData.notes}
                onChange={e => setFormData({...formData, notes: e.target.value})}
                placeholder="Credentials, specializations, or special notes..."
                rows={2}
                className="mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">Store vendor credentials or account details for your reference.</p>
            </div>
          </div>
          <DialogFooter className="flex-col gap-3 sm:flex-col sm:items-stretch">
            <label className="flex items-start gap-2 px-3 py-2.5 rounded-md bg-slate-50 border border-slate-200 cursor-pointer hover:bg-slate-100 transition-colors">
              <input
                type="checkbox"
                checked={openInOutlook}
                onChange={(e) => setOpenInOutlook(e.target.checked)}
                disabled={skipSetupEmail}
                className="mt-0.5 accent-violet-600 cursor-pointer disabled:cursor-not-allowed"
                data-testid="contractor-outlook-toggle"
              />
              <span className={`text-xs leading-snug ${skipSetupEmail ? "text-slate-400" : "text-slate-700"}`}>
                <strong className={skipSetupEmail ? "text-slate-400" : "text-slate-900"}>Also open in Outlook</strong> — autosends through AskIsaac and ALSO pops a pre-filled Outlook draft so you can review or personally sign before it goes out.
              </span>
            </label>
            <label className="flex items-start gap-2 px-3 py-2.5 rounded-md bg-amber-50 border border-amber-200 cursor-pointer hover:bg-amber-100 transition-colors">
              <input
                type="checkbox"
                checked={skipSetupEmail}
                onChange={(e) => setSkipSetupEmail(e.target.checked)}
                className="mt-0.5 accent-amber-600 cursor-pointer"
                data-testid="contractor-skip-email-toggle"
              />
              <span className="text-xs text-amber-900 leading-snug">
                <strong className="text-amber-950">Manual add — skip activation email</strong> — use this when you&apos;re onboarding a contractor you already work with offline. The account is created and auto-approved without sending them a setup email.
              </span>
            </label>
            <div className="flex items-center justify-end gap-2">
              <Button variant="outline" onClick={() => setShowAddModal(false)}>Cancel</Button>
              <Button
                onClick={handleAddContractor}
                disabled={!formData.name || !formData.company_name || !formData.email}
                className={skipSetupEmail ? "bg-amber-600 hover:bg-amber-700" : "bg-violet-600 hover:bg-violet-700"}
                data-testid="contractor-add-submit-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                {skipSetupEmail
                  ? "Add & Approve (No Email)"
                  : openInOutlook
                    ? "Send & Open Outlook"
                    : "Email Setup Link"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Setup Link Confirmation Modal */}
      <Dialog open={setupLinkModal} onOpenChange={setSetupLinkModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {setupLinkData?.email_sent ? (
                <>
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                  Setup Email Sent!
                </>
              ) : (
                <>
                  <Link className="w-5 h-5 text-violet-500" />
                  Setup Link Generated
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {setupLinkData?.email_sent 
                ? `A setup email has been sent to ${setupLinkData?.contractor?.email}`
                : `Send this link to ${setupLinkData?.contractor?.company_name} to complete their account setup.`
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {setupLinkData?.email_sent && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                <p className="text-emerald-700 text-sm flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Setup invitation emailed to <strong>{setupLinkData?.contractor?.email}</strong>
                </p>
              </div>
            )}
            
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-xs text-slate-500 mb-2">Contractor</p>
              <p className="font-semibold text-slate-900">{setupLinkData?.contractor?.company_name}</p>
              <p className="text-sm text-slate-600">{setupLinkData?.contractor?.email}</p>
            </div>
            
            <div>
              <Label className="text-slate-600">Setup Link</Label>
              <div className="mt-1 flex gap-2">
                <Input 
                  value={setupLinkData?.setup_link || ""} 
                  readOnly 
                  className="bg-white font-mono text-xs"
                />
                <Button variant="outline" onClick={() => {
                  navigator.clipboard.writeText(setupLinkData?.setup_link || "");
                  toast.success("Setup link copied!");
                }}>
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-2">This link expires in 7 days.</p>
            </div>

            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
              <p className="text-emerald-700 text-sm flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Click &quot;Send Setup Link&quot; to open your email client with a pre-written invite.
              </p>
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setSetupLinkModal(false)}>Close</Button>
            <Button 
              variant="outline"
              onClick={() => window.open(setupLinkData?.setup_link, '_blank')} 
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Preview Link
            </Button>
            <Button 
              onClick={() => {
                const contractorName = setupLinkData?.contractor?.company_name || "Contractor";
                const contactName = setupLinkData?.contractor?.name || "";
                const email = setupLinkData?.contractor?.email || "";
                const setupLink = setupLinkData?.setup_link || "";
                
                const subject = encodeURIComponent(`Welcome to AskIsaac - Complete Your Account Setup`);
                const body = encodeURIComponent(
`Dear ${contactName || contractorName},

Welcome to the AskIsaac platform!

You have been invited to join AskIsaac as a verified contractor partner. To complete your account setup and start receiving job opportunities, please click the link below:

${setupLink}

WHAT TO EXPECT:
• Set your secure password
• Review and accept our terms of service
• Connect your Telegram for real-time job alerts (@askisaac_bot)
• Start receiving maintenance requests from hospitals

This link will expire in 7 days. If you have any questions, please contact our support team.

We look forward to working with you!

Best regards,
The AskIsaac Team
Sterile Mate LLC
www.sterilemate.com
info@sterilemate.com
`
                );
                
                window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
                toast.success("Opening email client...");
              }}
              className="bg-violet-600 hover:bg-violet-700"
            >
              <Mail className="w-4 h-4 mr-2" />
              Send Setup Link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dispatch Modal */}
      <Dialog open={showDispatchModal} onOpenChange={setShowDispatchModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Dispatch Ticket #{selectedTicket?.id}</DialogTitle>
            <DialogDescription>{selectedTicket?.type} at {selectedTicket?.hospital_name}</DialogDescription>
          </DialogHeader>
          
          {selectedTicket?.is_warranty && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-amber-700 mb-2">
                <Shield className="w-5 h-5" />
                <span className="font-medium">Warranty Job</span>
              </div>
              <p className="text-sm text-amber-600 mb-3">This is covered under warranty. Generate an override code to allow billing.</p>
              {warrantyOverrideCode ? (
                <div className="bg-white rounded p-2 text-center">
                  <p className="text-xs text-slate-500">Override Code</p>
                  <p className="font-mono text-lg font-bold text-amber-700">{warrantyOverrideCode}</p>
                </div>
              ) : (
                <Button size="sm" variant="outline" onClick={generateWarrantyOverride} disabled={generatingOverride}>
                  {generatingOverride ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Generate Override Code
                </Button>
              )}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <Label>Select Contractors</Label>
              <div className="mt-2 space-y-2 max-h-48 overflow-y-auto">
                {contractors.filter(c => c.is_available).map(c => (
                  <label key={c.id} className="flex items-center gap-3 p-2 rounded hover:bg-slate-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedContractors.includes(c.id)}
                      onChange={e => {
                        if (e.target.checked) setSelectedContractors([...selectedContractors, c.id]);
                        else setSelectedContractors(selectedContractors.filter(id => id !== c.id));
                      }}
                      className="rounded"
                    />
                    <div>
                      <p className="font-medium text-sm">{c.name}</p>
                      <p className="text-xs text-slate-500">{c.company_name}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label>Dispatch Notes</Label>
              <Textarea value={dispatchNotes} onChange={e => setDispatchNotes(e.target.value)} placeholder="Special instructions..." />
            </div>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={isEmergency} onChange={e => setIsEmergency(e.target.checked)} className="rounded" />
              <span className="text-sm text-red-600 font-medium">Mark as Emergency</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDispatchModal(false)}>Cancel</Button>
            <Button onClick={handleDispatch} className="bg-blue-600 hover:bg-blue-700">
              <Send className="w-4 h-4 mr-2" />
              Dispatch
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Job Modal */}
      <Dialog open={showAssignJobModal} onOpenChange={setShowAssignJobModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-blue-600" />
              Assign Job to Contractor
            </DialogTitle>
            <DialogDescription>
              Assign a maintenance request or project to {selectedContractorForAssign?.name || selectedContractorForAssign?.company_name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Contractor Info */}
            <div className="bg-slate-50 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <Briefcase className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-semibold text-slate-900">{selectedContractorForAssign?.name}</p>
                  <p className="text-sm text-slate-500">{selectedContractorForAssign?.company_name}</p>
                </div>
              </div>
            </div>

            {/* Job Type Selection */}
            <div>
              <Label>Job Type</Label>
              <div className="flex gap-2 mt-2">
                <Button
                  type="button"
                  variant={assignJobType === "maintenance" ? "default" : "outline"}
                  className={assignJobType === "maintenance" ? "bg-blue-600" : ""}
                  onClick={() => handleJobTypeChange("maintenance")}
                >
                  <Wrench className="w-4 h-4 mr-2" />
                  Maintenance
                </Button>
                <Button
                  type="button"
                  variant={assignJobType === "project" ? "default" : "outline"}
                  className={assignJobType === "project" ? "bg-blue-600" : ""}
                  onClick={() => handleJobTypeChange("project")}
                >
                  <ClipboardList className="w-4 h-4 mr-2" />
                  Project
                </Button>
              </div>
            </div>

            {/* Job Selection */}
            <div>
              <Label>Select {assignJobType === "maintenance" ? "Maintenance Request" : "Project"} *</Label>
              {loadingJobs ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                </div>
              ) : (
                <Select value={selectedJobId} onValueChange={setSelectedJobId}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder={`Select a ${assignJobType}...`} />
                  </SelectTrigger>
                  <SelectContent>
                    {availableJobs.length > 0 ? (
                      availableJobs.map(job => (
                        <SelectItem key={job.id} value={job.id}>
                          {job.label}
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="none" disabled>
                        No {assignJobType === "maintenance" ? "requests" : "projects"} available
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Assignment Notes */}
            <div>
              <Label>Assignment Notes</Label>
              <Textarea
                value={assignNotes}
                onChange={(e) => setAssignNotes(e.target.value)}
                placeholder="Any special instructions for this assignment..."
                className="mt-1"
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignJobModal(false)}>Cancel</Button>
            <Button 
              onClick={() => handleAssignJob(false)} 
              disabled={!selectedJobId || loadingJobs}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="confirm-assign-job-btn"
            >
              <Briefcase className="w-4 h-4 mr-2" />
              Assign Job
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contractor Detail Modal */}
      <Dialog open={showContractorDetail} onOpenChange={setShowContractorDetail}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-slate-600" />
              Contractor Details
            </DialogTitle>
          </DialogHeader>

          {contractorDetail && (
            <div className="space-y-6">
              {/* Basic Info */}
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-lg text-slate-900">{contractorDetail.name}</h3>
                    <p className="text-slate-500">{contractorDetail.company_name}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    contractorDetail.is_available ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'
                  }`}>
                    {contractorDetail.is_available ? 'Available' : 'Busy'}
                  </span>
                </div>
                
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Mail className="w-4 h-4" />
                    {contractorDetail.email}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Phone className="w-4 h-4" />
                    {contractorDetail.phone || 'N/A'}
                  </div>
                  {contractorDetail.hourly_rate && (
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <DollarSign className="w-4 h-4" />
                      ${contractorDetail.hourly_rate}/hr
                    </div>
                  )}
                </div>

                {contractorDetail.categories?.length > 0 && (
                  <div className="mt-4">
                    <p className="text-sm text-slate-500 mb-2">Specialties</p>
                    <div className="flex flex-wrap gap-2">
                      {contractorDetail.categories.map(cat => (
                        <span key={cat} className="px-3 py-1 bg-blue-50 text-blue-600 rounded-full text-sm">
                          {cat}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Current Assignments */}
              <div>
                <h4 className="font-semibold text-slate-900 mb-3">Current Assignments</h4>
                {contractorDetail.assignments?.length > 0 ? (
                  <div className="space-y-2">
                    {contractorDetail.assignments.map((assignment, idx) => (
                      <div key={idx} className="bg-white border border-slate-200 rounded-lg p-3 flex items-center justify-between">
                        <div>
                          <p className="font-medium text-slate-900">
                            {assignment.type === 'maintenance' ? (
                              <span className="flex items-center gap-2">
                                <Wrench className="w-4 h-4 text-orange-500" />
                                {assignment.title || `Maintenance #${assignment.id}`}
                              </span>
                            ) : (
                              <span className="flex items-center gap-2">
                                <ClipboardList className="w-4 h-4 text-blue-500" />
                                {assignment.title || `Project #${assignment.id}`}
                              </span>
                            )}
                          </p>
                          <p className="text-sm text-slate-500">{assignment.hospital_name}</p>
                        </div>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          assignment.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                          assignment.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                          'bg-amber-100 text-amber-700'
                        }`}>
                          {assignment.status?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 bg-slate-50 rounded-lg text-slate-500">
                    No current assignments
                  </div>
                )}
              </div>

              {/* Documents & Verification (admin two-step approval) */}
              <div data-testid="contractor-verification-section">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-slate-900 flex items-center gap-2">
                    <Shield className="w-5 h-5 text-blue-600" />
                    Documents & Verification
                  </h4>
                  {fullProfile?.verification?.is_verified ? (
                    <span className="px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-sm font-semibold inline-flex items-center gap-1">
                      <CheckCircle2 className="w-4 h-4" /> Verified
                    </span>
                  ) : (
                    <span className="px-3 py-1 rounded-full bg-amber-100 text-amber-700 text-sm font-semibold inline-flex items-center gap-1">
                      <ShieldAlert className="w-4 h-4" /> {fullProfile?.verification?.verification_status === "pending_review" ? "Pending Review" : "Pending Documents"}
                    </span>
                  )}
                </div>

                {fullProfile?.verification?.verified_at && (
                  <p className="text-xs text-slate-500 mb-3">
                    Verified on {new Date(fullProfile.verification.verified_at).toLocaleDateString()} by {fullProfile.verification.verified_by || "admin"}
                  </p>
                )}

                <div className="space-y-2 mb-4">
                  {(fullProfile?.checklist || []).map((item) => (
                    <div key={item.type} className="flex items-center justify-between p-2 bg-white border border-slate-200 rounded-lg">
                      <div className="flex items-center gap-2">
                        {item.uploaded ? (
                          <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                        ) : (
                          <Clock className="w-4 h-4 text-amber-500" />
                        )}
                        <span className="text-sm text-slate-700">{item.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${item.uploaded ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                          {item.uploaded ? "Uploaded" : "Missing"}
                        </span>
                        <label className="cursor-pointer inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 px-2 py-1 rounded border border-blue-200 hover:bg-blue-50" data-testid={`upload-${item.type}-btn`}>
                          <FileSignature className="w-3 h-3" />
                          {item.uploaded ? "Replace" : "Upload"}
                          <input
                            type="file"
                            className="hidden"
                            accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xlsx,.xls"
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              if (f) uploadContractorDocument(f, item.type);
                              e.target.value = "";
                            }}
                          />
                        </label>
                      </div>
                    </div>
                  ))}
                  {!fullProfile?.checklist?.length && (
                    <div className="text-sm text-slate-500 text-center py-4">Loading documents…</div>
                  )}
                </div>

                {/* Free-form upload — additional certifications outside the required list */}
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-slate-700">
                    <FileSignature className="w-4 h-4 text-blue-600" />
                    Add an additional certification or document
                  </div>
                  <label className="cursor-pointer inline-flex items-center gap-1 text-xs text-white bg-blue-600 hover:bg-blue-700 px-3 py-1.5 rounded" data-testid="upload-extra-cert-btn">
                    + Upload File
                    <input
                      type="file"
                      className="hidden"
                      accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xlsx,.xls"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (!f) return;
                        const customName = window.prompt("Document name (e.g. 'OSHA 30 Card')", f.name) || f.name;
                        // Use a stable type slug derived from the name
                        const typeSlug = customName.toLowerCase().replace(/[^a-z0-9]+/g, "_").slice(0, 40) || "additional";
                        const fd = new FormData();
                        fd.append("file", f);
                        fd.append("document_type", typeSlug);
                        fd.append("document_name", customName);
                        fetch(`${API}/admin/contractors/${contractorDetail.id}/upload-document`, {
                          method: "POST",
                          headers: { Authorization: `Bearer ${token}` },
                          body: fd,
                        }).then(r => r.ok ? (toast.success("Document uploaded"), refreshFullProfile(contractorDetail.id)) : toast.error("Upload failed"));
                        e.target.value = "";
                      }}
                    />
                  </label>
                </div>

                {(fullProfile?.documents?.length || 0) > 0 && (
                  <div className="mb-4">
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">Uploaded files</p>
                    <div className="space-y-1">
                      {fullProfile.documents.map((d) => (
                        <div
                          key={d.id || d.document_id}
                          className="flex items-center justify-between p-2 bg-blue-50 hover:bg-blue-100 rounded-lg text-sm"
                        >
                          <a
                            href={d.file_url || d.url || "#"}
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center gap-2 text-slate-700 flex-1 min-w-0"
                          >
                            <FileText className="w-4 h-4 text-blue-600 shrink-0" />
                            <span className="truncate">{d.document_name || d.document_type || d.file_name || "Document"}</span>
                            {d.uploaded_by_role === "admin" && (
                              <span className="text-[10px] px-1.5 py-0 rounded bg-blue-200 text-blue-900">admin</span>
                            )}
                          </a>
                          <div className="flex items-center gap-1 shrink-0">
                            <a href={d.file_url || d.url || "#"} target="_blank" rel="noreferrer">
                              <Download className="w-4 h-4 text-blue-600" />
                            </a>
                            <button
                              onClick={() => deleteContractorDocument(d.id || d.document_id)}
                              className="text-red-500 hover:text-red-700"
                              title="Remove document"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex gap-2">
                  {fullProfile?.verification?.is_verified ? (
                    <Button
                      variant="outline"
                      className="flex-1 border-amber-200 text-amber-700 hover:bg-amber-50"
                      onClick={handleRevokeVerification}
                      disabled={verifying}
                      data-testid="revoke-verification-btn"
                    >
                      <ShieldAlert className="w-4 h-4 mr-2" />
                      Revoke Verified Status
                    </Button>
                  ) : (
                    <Button
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                      onClick={handleMarkVerified}
                      disabled={verifying || !fullProfile?.ready_for_admin_verify}
                      data-testid="mark-verified-btn"
                      title={!fullProfile?.ready_for_admin_verify ? "All required documents must be uploaded first" : "Mark contractor as fully verified"}
                    >
                      {verifying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                      Mark as Verified
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    onClick={() => openActivationEmail(contractorDetail)}
                    title="Send activation email from your Outlook"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Send Activation Email
                  </Button>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="flex gap-2">
                <Button
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  onClick={() => {
                    setShowContractorDetail(false);
                    openAssignJobModal(contractorDetail);
                  }}
                  data-testid="assign-job-btn"
                  title={
                    fullProfile?.verification?.is_verified
                      ? "Assign a new job to this contractor"
                      : "Contractor isn't verified yet — you'll be asked to confirm before the assignment goes through"
                  }
                >
                  <Briefcase className="w-4 h-4 mr-2" />
                  Assign New Job
                  {fullProfile && !fullProfile?.verification?.is_verified && (
                    <span className="ml-2 text-xs bg-amber-500/30 text-amber-100 px-1.5 py-0.5 rounded">override</span>
                  )}
                </Button>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowContractorDetail(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Contractor Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Contractor</DialogTitle>
            <DialogDescription>Update contractor information</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Contact Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="John Smith"
                />
              </div>
              <div>
                <Label>Company Name *</Label>
                <Input
                  value={formData.company_name}
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  placeholder="ABC Services"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="john@company.com"
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="(800) 988-3892"
                />
              </div>
            </div>
            <div>
              <Label>Address</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="123 Main St, City, State"
              />
            </div>
            <div>
              <Label>Hourly Rate ($)</Label>
              <Input
                type="number"
                value={formData.hourly_rate}
                onChange={(e) => setFormData({ ...formData, hourly_rate: e.target.value })}
                placeholder="75"
              />
            </div>
            <div>
              <Label>Categories</Label>
              <Select
                value={formData.categories?.[0] || ""}
                onValueChange={(val) => setFormData({ ...formData, categories: val ? [val] : [] })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowEditModal(false); setEditingContractor(null); }}>Cancel</Button>
            <Button onClick={handleEditContractor}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Contractor</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {contractorToDelete?.name || contractorToDelete?.company_name}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowDeleteConfirm(false); setContractorToDelete(null); }}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteContractor}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Contractor Modal */}
      <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-blue-600" />
              Email Contractor
            </DialogTitle>
            <DialogDescription>
              Pre-fills your default email client (Outlook, Apple Mail, Gmail) with a message to {emailContractor?.name} ({emailContractor?.email}). You hit Send from your own account — that&apos;s how it bypasses the Resend domain block.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Subject *</Label>
              <Input
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                placeholder="Enter email subject"
              />
            </div>
            <div>
              <Label>Message *</Label>
              <Textarea
                value={emailBody}
                onChange={(e) => setEmailBody(e.target.value)}
                placeholder="Enter your message..."
                rows={6}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowEmailModal(false); setEmailContractor(null); }}>Cancel</Button>
            <Button onClick={handleSendEmail} disabled={!emailSubject || !emailBody} data-testid="open-in-outlook-btn">
              <Mail className="w-4 h-4 mr-2" />
              Open in My Outlook
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Agreement Invitation Modal */}
      <Dialog open={showAgreementModal} onOpenChange={(open) => !open && resetAgreementModal()}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSignature className="w-5 h-5 text-emerald-600" />
              Send Contractor Agreement
            </DialogTitle>
            <DialogDescription>
              Invite a new contractor to review and sign the Independent Contractor Agreement. They will receive an email with a secure link.
            </DialogDescription>
          </DialogHeader>

          {agreementResult ? (
            // Success View
            <div className="py-4 space-y-4">
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                <div className="flex items-center gap-3 mb-3">
                  <CheckCircle className="w-8 h-8 text-emerald-500" />
                  <div>
                    <h3 className="font-semibold text-emerald-900">Agreement Invitation Sent!</h3>
                    <p className="text-sm text-emerald-700">
                      {agreementResult.email_sent ? "Email sent to contractor" : "Agreement link generated"}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                <div>
                  <p className="text-xs text-slate-500">Contractor</p>
                  <p className="font-semibold text-slate-900">{agreementFormData.contractor_company}</p>
                  <p className="text-sm text-slate-600">{agreementFormData.contractor_name}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Agreement Signing Link</p>
                  <div className="flex gap-2 mt-1">
                    <Input value={agreementResult.signing_url || ""} readOnly className="font-mono text-xs" />
                    <Button variant="outline" size="sm" onClick={() => {
                      navigator.clipboard.writeText(agreementResult.signing_url || "");
                      toast.success("Link copied!");
                    }}>
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>

              <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-3">
                <p className="text-cyan-800 text-sm">
                  <strong>Next Steps:</strong> The contractor will review and sign the agreement, then upload compliance documents (licenses, insurance, W-9).
                </p>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => window.open(agreementResult.signing_url, '_blank')}>
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Preview Link
                </Button>
                <Button onClick={resetAgreementModal} className="bg-emerald-600 hover:bg-emerald-700">
                  Done
                </Button>
              </DialogFooter>
            </div>
          ) : (
            // Form View
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Contact Name *</Label>
                  <Input 
                    value={agreementFormData.contractor_name} 
                    onChange={e => setAgreementFormData({...agreementFormData, contractor_name: e.target.value})} 
                    placeholder="e.g. John Smith" 
                    data-testid="agreement-contractor-name"
                  />
                </div>
                <div>
                  <Label>Company Name *</Label>
                  <Input 
                    value={agreementFormData.contractor_company} 
                    onChange={e => setAgreementFormData({...agreementFormData, contractor_company: e.target.value})} 
                    placeholder="e.g. ABC Plumbing Services" 
                    data-testid="agreement-company-name"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Email *</Label>
                  <Input 
                    type="email" 
                    value={agreementFormData.contractor_email} 
                    onChange={e => setAgreementFormData({...agreementFormData, contractor_email: e.target.value})} 
                    placeholder="contractor@company.com" 
                    data-testid="agreement-email"
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input 
                    value={agreementFormData.contractor_phone} 
                    onChange={e => setAgreementFormData({...agreementFormData, contractor_phone: e.target.value})} 
                    placeholder="(281) 456-3458" 
                  />
                </div>
              </div>

              <div>
                <Label>Business Address</Label>
                <Input 
                  value={agreementFormData.contractor_address} 
                  onChange={e => setAgreementFormData({...agreementFormData, contractor_address: e.target.value})} 
                  placeholder="123 Main St, Houston, TX 77001" 
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Trade/Service Type</Label>
                  <Select 
                    value={agreementFormData.trade_type} 
                    onValueChange={val => setAgreementFormData({...agreementFormData, trade_type: val})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select trade" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Electrical">Electrical</SelectItem>
                      <SelectItem value="Plumbing">Plumbing</SelectItem>
                      <SelectItem value="HVAC">HVAC</SelectItem>
                      <SelectItem value="General Contractor">General Contractor</SelectItem>
                      <SelectItem value="Water Treatment">Water Treatment</SelectItem>
                      <SelectItem value="Fire Safety">Fire Safety</SelectItem>
                      <SelectItem value="Medical Equipment">Medical Equipment</SelectItem>
                      <SelectItem value="Demolition">Demolition</SelectItem>
                      <SelectItem value="Concrete">Concrete</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Notes (Internal)</Label>
                <Textarea
                  value={agreementFormData.notes}
                  onChange={e => setAgreementFormData({...agreementFormData, notes: e.target.value})}
                  placeholder="Any additional notes about this contractor..."
                  rows={2}
                />
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-4">
                <div className="flex items-start gap-2">
                  <FileText className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="text-amber-800 text-sm font-medium">What Happens Next</p>
                    <ol className="text-amber-700 text-sm mt-1 space-y-1 list-decimal list-inside">
                      <li>Contractor receives email with agreement link</li>
                      <li>They review and digitally sign the agreement</li>
                      <li>Signed PDF is emailed to both parties</li>
                      <li>Contractor uploads compliance documents</li>
                    </ol>
                  </div>
                </div>
              </div>

              <DialogFooter className="pt-4">
                <Button variant="outline" onClick={resetAgreementModal}>Cancel</Button>
                <Button 
                  onClick={handleSendAgreementInvitation} 
                  disabled={sendingAgreement || !agreementFormData.contractor_name || !agreementFormData.contractor_company || !agreementFormData.contractor_email}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="send-agreement-btn"
                >
                  {sendingAgreement ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Agreement Invitation
                    </>
                  )}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <SupplierLedgerDialog open={!!ledgerContractor} onClose={() => setLedgerContractor(null)}
        supplierId={ledgerContractor?.id} supplierName={ledgerContractor?.name} token={token} />
    </div>
  );
}
